import React, { useEffect, useState } from "react";
import Select from "react-select";
import { CustomerPart } from "../models/customerparts";
import "../styles/AddItemForm.css";

interface AddCustomerPartFormProps {
    onSave: (customerPart: CustomerPart) => Promise<void>;
    onCancel: () => void;
    customers: any[];
    isProcessing?: boolean;
    isEditing?: boolean;
    editingCustomerPart?: CustomerPart | null;
}

const AddCustomerPartForm: React.FC<AddCustomerPartFormProps> = ({
    onSave,
    onCancel,
    customers,
    isProcessing,
    isEditing,
    editingCustomerPart,
}) => {
    const [customerPart, setCustomerPart] = useState<CustomerPart>({
        customerId: "",
        partNo: "",
        partName: "",
        unitPrice: 0,
        gst: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        posCode: "",
        status: "ACTIVE",
    });

    // Add field states to store string values for numeric inputs
    const [inputValues, setInputValues] = useState({
        unitPrice: "0",
        gst: "0",
        sgst: "0",
        cgst: "0",
        igst: "0"
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string; posCode?: string }[]>([]);

    useEffect(() => {
        console.log("customers: ", customers);

        if (customers.length > 0) {
            const options = customers.map((customer) => ({
                value: customer._id,
                label: customer.name,
                posCode: customer.posCode,
            }));
            setCustomerOptions(options);
        }

        if (isEditing && editingCustomerPart) {
            setCustomerPart(editingCustomerPart);
            // Initialize input field values from editing part
            setInputValues({
                unitPrice: editingCustomerPart.unitPrice.toString(),
                gst: editingCustomerPart.gst.toString(),
                sgst: editingCustomerPart.sgst.toString(),
                cgst: editingCustomerPart.cgst.toString(),
                igst: editingCustomerPart.igst.toString()
            });
        } else {
            setCustomerPart({
                customerId: "",
                partNo: "",
                partName: "",
                unitPrice: 0,
                gst: 0,
                sgst: 0,
                cgst: 0,
                igst: 0,
                posCode: "",
                status: "ACTIVE",
            });
            setInputValues({
                unitPrice: "0",
                gst: "0",
                sgst: "0",
                cgst: "0",
                igst: "0"
            });
        }
        
        // Clear any previous errors when component mounts/remounts
        setErrors({});
    }, [isEditing, editingCustomerPart, customers]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
    
        // Check if the field should be numeric
        const numericFields = ["unitPrice", "gst", "sgst", "cgst", "igst"];
    
        if (numericFields.includes(name)) {
            // Handle numeric fields
            
            // Empty field handling
            if (value === "") {
                setInputValues(prev => ({
                    ...prev,
                    [name]: ""
                }));
                setCustomerPart(prev => ({
                    ...prev,
                    [name]: 0
                }));
                setErrors(prev => ({ ...prev, [name]: "" }));
                return;
            }
            
            // Decimal validation
            const decimalRegex = name === "unitPrice" 
                ? /^\d*\.?\d{0,2}$/ // For unitPrice: up to 2 decimal places
                : /^\d*\.?\d*$/;    // For other numeric fields: any decimal
                
            // Allow typing decimal points and numbers
            if (decimalRegex.test(value)) {
                // Update the string representation first
                setInputValues(prev => ({
                    ...prev,
                    [name]: value
                }));
                
                // Only update numeric value if it's a valid number
                if (value !== ".") {
                    const parsedValue = parseFloat(value);
                    
                    // Additional validations
                    if (!isNaN(parsedValue)) {
                        // For tax fields, ensure they don't exceed 100%
                        if ((name === "gst" || name === "sgst" || name === "cgst" || name === "igst") && 
                            parsedValue > 100) {
                            setErrors(prev => ({ ...prev, [name]: "Cannot exceed 100%" }));
                            return;
                        }
                        
                        // Update the actual numeric value in the state
                        setCustomerPart(prev => ({
                            ...prev,
                            [name]: parsedValue
                        }));
                        setErrors(prev => ({ ...prev, [name]: "" }));
                    }
                } else {
                    // When user just types ".", keep the numeric value as 0 until more digits added
                    setCustomerPart(prev => ({
                        ...prev,
                        [name]: 0
                    }));
                }
            }
        } else {
            // Handle non-numeric fields normally
            setCustomerPart(prev => ({
                ...prev,
                [name]: value
            }));
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };
    
    const handleSelectChange = (selectedOption: any, { name }: any) => {
        setCustomerPart((prev) => {
            const updatedPart = {
                ...prev,
                [name]: selectedOption?.value || "",
            };

            // Auto-fill posCode when a customer is selected
            if (name === "customerId") {
                const selectedCustomer = customers.find((customer) => customer._id === selectedOption?.value);
                if (selectedCustomer) {
                    updatedPart.posCode = selectedCustomer.posCode || "";
                }
            }

            return updatedPart;
        });
        
        // Clear error for select fields
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        // Required fields validation
        if (!customerPart.customerId) {
            newErrors.customerId = "Required";
        }

        if (!customerPart.partNo) {
            newErrors.partNo = "Required";
        }

        if (!customerPart.partName) {
            newErrors.partName = "Required";
        }

        if (!customerPart.posCode) {
            newErrors.posCode = "Required";
        }

        // Numeric fields validation - checking for zero values
        const numericFields: (keyof CustomerPart)[] = ["unitPrice", "gst", "sgst", "cgst", "igst"];
        numericFields.forEach(field => {
            const value = customerPart[field];
            // Require unitPrice to be greater than 0
            if (field === "unitPrice" && (value === 0 || value === "")) {
                newErrors[field] = "Required";
            }
            
            // Additional validation for tax fields - non-negative
            if (typeof value === "number" && value < 0) {
                newErrors[field] = "Cannot be negative";
            }
            
            // GST validation - should be between 0 and 100
            if ((field === "gst" || field === "sgst" || field === "cgst" || field === "igst") && 
                typeof value === "number" && value > 100) {
                newErrors[field] = "Cannot exceed 100%";
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate form before submission
        if (validateForm()) {
            onSave(customerPart);
        }
    };

    const statusOptions = [
        { value: "ACTIVE", label: "ACTIVE" },
        { value: "INACTIVE", label: "INACTIVE" },
    ];

    return (
        <>
            <div className="breadcrumb">
                <span className="breadcrumb-item" onClick={onCancel}>
                    Customer Part
                </span>
                <span className="breadcrumb-separator"> &gt; </span>
                <span className="breadcrumb-current">
                    {isEditing ? "Edit Customer Part" : "Add Customer Part"}
                </span>
            </div>

            <div className="form-container">
                <form onSubmit={handleSubmit} className="add-item-form" noValidate>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Customer :<span className="required">*</span></label>
                            <Select
                                name="customerId"
                                options={customerOptions}
                                value={customerOptions.find((o) => o.value === customerPart.customerId)}
                                onChange={(selectedOption) => {
                                    const selectedCustomer = customers.find((c) => c._id === selectedOption?.value);
                                    setCustomerPart((prev) => ({
                                        ...prev,
                                        customerId: selectedOption?.value || "",
                                        posCode: selectedCustomer?.posCode || "",
                                    }));
                                    setErrors((prev) => ({ ...prev, customerId: "", posCode: "" }));
                                }}
                                placeholder="Select Customer"
                                className={`react-select-container ${errors.customerId ? "error-select" : ""}`}
                                classNamePrefix="react-select"
                            />
                            {errors.customerId && <span className="error-message">{errors.customerId}</span>}
                        </div>
                        <div className="form-group">
                            <label>Part No :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="partNo" 
                                value={customerPart.partNo} 
                                onChange={handleChange} 
                                className={errors.partNo ? "error-input" : ""}
                            />
                            {errors.partNo && <span className="error-message">{errors.partNo}</span>}
                        </div>
                        <div className="form-group">
                            <label>Part Name :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="partName" 
                                value={customerPart.partName} 
                                onChange={handleChange} 
                                className={errors.partName ? "error-input" : ""}
                            />
                            {errors.partName && <span className="error-message">{errors.partName}</span>}
                        </div>
                        <div className="form-group">
                            <label>Unit Price :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="unitPrice" 
                                value={inputValues.unitPrice} 
                                onChange={handleChange} 
                                placeholder="0.00"
                                className={errors.unitPrice ? "error-input" : ""}
                            />
                            {errors.unitPrice && <span className="error-message">{errors.unitPrice}</span>}
                        </div>
                        <div className="form-group">
                            <label>GST :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="gst" 
                                value={inputValues.gst} 
                                onChange={handleChange} 
                                className={errors.gst ? "error-input" : ""}
                            />
                            {errors.gst && <span className="error-message">{errors.gst}</span>}
                        </div>
                        <div className="form-group">
                            <label>SGST :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="sgst" 
                                value={inputValues.sgst} 
                                onChange={handleChange} 
                                className={errors.sgst ? "error-input" : ""}
                            />
                            {errors.sgst && <span className="error-message">{errors.sgst}</span>}
                        </div>
                        <div className="form-group">
                            <label>CGST :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="cgst" 
                                value={inputValues.cgst} 
                                onChange={handleChange} 
                                className={errors.cgst ? "error-input" : ""}
                            />
                            {errors.cgst && <span className="error-message">{errors.cgst}</span>}
                        </div>
                        <div className="form-group">
                            <label>IGST :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="igst" 
                                value={inputValues.igst} 
                                onChange={handleChange} 
                                className={errors.igst ? "error-input" : ""}
                            />
                            {errors.igst && <span className="error-message">{errors.igst}</span>}
                        </div>
                        <div className="form-group">
                            <label>POS Code :<span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="posCode" 
                                value={customerPart.posCode} 
                                onChange={handleChange} 
                                className={errors.posCode ? "error-input" : ""}
                            />
                            {errors.posCode && <span className="error-message">{errors.posCode}</span>}
                        </div>
                        <div className="form-group">
                            <label>Status :<span className="required">*</span></label>
                            <Select
                                name="status"
                                options={statusOptions}
                                value={statusOptions.find((o) => o.value === customerPart.status)}
                                onChange={handleSelectChange}
                                className={`react-select-container ${errors.status ? "error-select" : ""}`}
                                classNamePrefix="react-select"
                            />
                            {errors.status && <span className="error-message">{errors.status}</span>}
                        </div>
                    </div>

                    <div className="button-container">
                        <button type="button" className="button cancel-button" onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="submit" className="button save-button" disabled={isProcessing}>
                            {isProcessing ? "Saving..." : isEditing ? "Update Customer Part" : "Add Customer Part"}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AddCustomerPartForm;